import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ArrowRight, X, Star } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import {
  serviceProducts,
  filterAndSortProducts,
  getProductBySlug,
  getProductPriceRange,
  getProductImage,
} from '../../data/serviceProducts';
import { getProductsForTranslationType } from '../../data/navServices';
import { mainServices, getMainService, serviceName } from '../../data/mainServices';
import ServicePriceRows from '../common/ServicePriceRows';
import assetUrl from '../../assetUrl';
import './ServiceCatalog.css';

const PAGE_SIZE = 12;

// Options de filtre = services fixes du JSON
const FILTER_ORDER = ['all', ...mainServices.map((s) => s.slug)];

function StarRating({ score = 5, count }) {
  return (
    <div className="service-product-stars" aria-label={`${score} / 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={12}
          className={i < Math.round(score) ? 'filled' : ''}
          fill={i < Math.round(score) ? 'currentColor' : 'none'}
        />
      ))}
      {count != null && count > 0 && (
        <span className="service-product-stars-count">{count}</span>
      )}
    </div>
  );
}

function ProductCard({ product, t, locale, index }) {
  const copy = t.serviceCatalog.products[product.id];
  const range = getProductPriceRange(product);
  const image = getProductImage(product);

  return (
    <Link
      to={`/services/${product.slug}`}
      className="service-product-card"
      id={product.slug}
      style={{ '--card-i': index }}
    >
      <div className="service-product-media">
        <img
          src={assetUrl(`${image}?v=12`)}
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="service-product-card-top">
        <StarRating score={5} count={product.rating} />
      </div>

      <h3>{copy?.title}</h3>
      {copy?.short && <p className="service-product-desc">{copy.short}</p>}

      <div className="service-product-card-foot">
        <ServicePriceRows
          service={{ price: range.min, unit: product.unit }}
          locale={locale}
        />
        <span className="service-product-order">
          {t.serviceCatalog.learnMore}
          <ArrowRight size={15} />
        </span>
      </div>
    </Link>
  );
}

function getTypeLabel(typeId, t, lang) {
  if (typeId === 'all') return t.serviceCatalog.allCategories;
  if (typeId === 'interpreting') return t.nav.interpreting;
  const jsonName = serviceName(getMainService(typeId), lang);
  return (
    jsonName
    || t.nav.servicesMenu?.[typeId]
    || t.serviceTypes?.types?.[typeId]?.title
    || typeId
  );
}

export default function ServiceCatalog({ forcedType, hideTypeFilters = false }) {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const paramType = searchParams.get('type') || 'all';
  const activeType = forcedType || paramType;
  const validType = FILTER_ORDER.includes(activeType) ? activeType : 'all';

  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';
  const catalog = t.serviceCatalog;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [debouncedQuery, validType]);

  const getProductCopy = useCallback((id) => catalog.products[id], [catalog.products]);

  const typeScopedProducts = useMemo(
    () => getProductsForTranslationType(validType, serviceProducts),
    [validType],
  );

  const filteredProducts = useMemo(
    () => filterAndSortProducts(typeScopedProducts, {
      category: 'all',
      query: debouncedQuery,
      sort: 'popular',
      getProductCopy,
    }),
    [typeScopedProducts, debouncedQuery, getProductCopy],
  );

  const shown = filteredProducts.slice(0, visible);
  const hasMore = visible < filteredProducts.length;

  const setType = (typeId) => {
    const next = new URLSearchParams(searchParams);
    if (!typeId || typeId === 'all') next.delete('type');
    else next.set('type', typeId);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return undefined;
    const product = getProductBySlug(hash);
    if (product) navigate(`/services/${hash}`, { replace: true });
    return undefined;
  }, [navigate]);

  // Filtres = les 19 services fixes du services.json
  const filterOptions = useMemo(
    () => ['all', ...mainServices.map((s) => s.slug)],
    [],
  );

  return (
    <section className="service-catalog">
      <div className="container">
        {!hideTypeFilters && (
          <div className="service-catalog-filters" role="tablist" aria-label={catalog.typesLabel}>
            {filterOptions.map((typeId) => {
              const selected = validType === typeId;
              return (
                <button
                  key={typeId}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`service-catalog-filter${selected ? ' is-active' : ''}`}
                  onClick={() => setType(typeId)}
                >
                  {getTypeLabel(typeId, t, lang)}
                </button>
              );
            })}
          </div>
        )}

        <div className="service-catalog-toolbar">
          <div className="service-catalog-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={catalog.searchPlaceholder}
              aria-label={catalog.searchPlaceholder}
            />
            {query && (
              <button
                type="button"
                className="service-catalog-search-clear"
                onClick={() => setQuery('')}
                aria-label={catalog.clearSearch}
              >
                <X size={16} />
              </button>
            )}
          </div>
          <p className="service-catalog-count">
            <strong>{filteredProducts.length}</strong> {catalog.productCount}
          </p>
        </div>

        {!hideTypeFilters && validType !== 'all' && (
          <p className="service-catalog-active-type">
            {getTypeLabel(validType, t, lang)}
            <button type="button" onClick={() => setType('all')}>
              {catalog.clearFilter}
            </button>
          </p>
        )}

        {filteredProducts.length === 0 ? (
          <div className="service-catalog-empty">
            <div className="service-catalog-empty-icon">
              <Search size={28} />
            </div>
            <h3>{catalog.noResults}</h3>
            <p>{catalog.noResultsHint}</p>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                setQuery('');
                setType('all');
              }}
            >
              {catalog.resetFilters}
            </button>
          </div>
        ) : (
          <>
            <div className="service-product-grid">
              {shown.map((product, index) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  t={t}
                  locale={locale}
                  index={index % PAGE_SIZE}
                />
              ))}
            </div>
            {hasMore && (
              <div className="service-catalog-more">
                <button
                  type="button"
                  className="service-catalog-more-btn"
                  onClick={() => setVisible((n) => n + PAGE_SIZE)}
                >
                  {catalog.seeMore}
                  <span>{filteredProducts.length - visible}</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
